import { HttpParams, type HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import type { CapabilitySuggestion } from '@indutrust/capability-model';
import { resolveHttpError, withRequestStatus } from '@indutrust/shared-util';
import { mapResponse } from '@ngrx/operators';
import { signalStore, withComputed, withState } from '@ngrx/signals';
import {
  Events,
  on,
  withEventHandlers,
  withReducer,
} from '@ngrx/signals/events';
import { switchMap } from 'rxjs';
import { CapabilitySuggestionApiClient } from '../capability-suggestion-api-client.service';
import { capabilitySuggestionEvents } from './capability-suggestion.events';

type CapabilitySuggestionState = {
  factoryId: number | null;
  suggestions: CapabilitySuggestion[];
};

const initialState: CapabilitySuggestionState = {
  factoryId: null,
  suggestions: [],
};

/**
 * Factory-scoped store of capability suggestions (leaf or parent) submitted
 * for this factory. Provided by
 * {@link import('@indutrust/factory-data').provideFactoryEditStores}.
 *
 * Loads via `GET /capability_suggestions?factory_id=`. In-House Capabilities
 * posts via {@link capabilitySuggestionEvents.addRequested}; machine-park
 * dual-write POSTs go through {@link CapabilitySuggestionApiClient} directly
 * from {@link import('@indutrust/factory-data').MachineParkStore}, then this
 * store reloads alongside the machine list after mutations.
 *
 * @example
 * const store = inject(CapabilitySuggestionStore);
 * const dispatch = injectDispatch(capabilitySuggestionEvents);
 * dispatch.loadRequested({ factoryId: 7 });
 */
export const CapabilitySuggestionStore = signalStore(
  withState(initialState),
  withRequestStatus(),
  withComputed(({ suggestions }) => ({
    entities: computed(() => suggestions()),
    pendingSuggestions: computed(() =>
      suggestions().filter((entry) => entry.status === 'pending')
    ),
  })),
  withReducer(
    on(capabilitySuggestionEvents.loadRequested, ({ payload }) => ({
      factoryId: payload.factoryId,
      requestStatus: 'loading' as const,
    })),
    on(capabilitySuggestionEvents.loadSucceeded, ({ payload }) => ({
      suggestions: payload.suggestions,
      requestStatus: 'success' as const,
    })),
    on(capabilitySuggestionEvents.loadFailed, ({ payload }) => ({
      requestStatus: { error: payload.error },
    })),
    on(capabilitySuggestionEvents.addRequested, () => ({
      requestStatus: 'loading' as const,
    })),
    on(capabilitySuggestionEvents.addSucceeded, ({ payload }, state) => ({
      suggestions: [
        ...(state as CapabilitySuggestionState).suggestions.filter(
          (entry) => entry.id !== payload.suggestion.id
        ),
        payload.suggestion,
      ],
      requestStatus: 'success' as const,
    })),
    on(capabilitySuggestionEvents.addFailed, ({ payload }) => ({
      requestStatus: { error: payload.error },
    }))
  ),
  withEventHandlers(
    (
      _store,
      events = inject(Events),
      client = inject(CapabilitySuggestionApiClient)
    ) => ({
      load$: events.on(capabilitySuggestionEvents.loadRequested).pipe(
        switchMap(({ payload }) =>
          client
            .getAll(
              undefined,
              new HttpParams().set('factory_id', String(payload.factoryId))
            )
            .pipe(
              mapResponse({
                next: (suggestions) =>
                  capabilitySuggestionEvents.loadSucceeded({
                    factoryId: payload.factoryId,
                    suggestions,
                  }),
                error: (error: HttpErrorResponse) =>
                  capabilitySuggestionEvents.loadFailed({
                    error: resolveHttpError(error).code,
                  }),
              })
            )
        )
      ),

      add$: events.on(capabilitySuggestionEvents.addRequested).pipe(
        switchMap(({ payload }) =>
          client
            .add({
              kind: payload.kind,
              name: payload.name,
              description: payload.description,
              ...(typeof payload.parentId === 'number'
                ? { parent_id: payload.parentId }
                : {}),
              status: 'pending',
              submitted_by_factory_id: payload.factoryId,
              ...(typeof payload.machineParkId === 'number'
                ? { submitted_by_machine_park_id: payload.machineParkId }
                : {}),
            })
            .pipe(
              mapResponse({
                next: (suggestion) =>
                  capabilitySuggestionEvents.addSucceeded({ suggestion }),
                error: (error: HttpErrorResponse) =>
                  capabilitySuggestionEvents.addFailed({
                    error: resolveHttpError(error).code,
                  }),
              })
            )
        )
      ),
    })
  )
);
